import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Card, CardActionArea, CircularProgress, Collapse, Stack, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import recordsService from '../../services/recordsService';
import formatFieldValue, { EMPTY_VALUE } from './formatFieldValue';
import RecordDetailModal from './RecordDetailModal';
import SECTION_TITLES from './sectionTitles';
import VehicleServiceMap from './serviceMap/VehicleServiceMap';

const TIMEFRAME_GROUPS = [
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'past', label: 'Past' },
];

// A vehicle card is headed by the name a member knows the car by, built from whichever of these the company shows.
const VEHICLE_NAME_FIELDS = ['year', 'make', 'model'];

// Row text wraps to a second line before it's cut. WHY: on a phone one line holds ~35 characters, and a single-line
// ellipsis hid the part that matters ("Description: Full Service Synthetic Oil Chan…").
const TWO_LINES = { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', overflowWrap: 'anywhere' };

/**
 * One record section of the member's page, e.g. "Your visits".
 *
 * Each row shows the first two configured fields; opening a row shows all of them. A section with no records renders
 * nothing — the same as offers and locations do — so a member without, say, a vehicle on file doesn't see an empty
 * box for it.
 *
 * On the membership page it previews the most recent few (`previewLimit`) with a "See all" link to the section's own
 * page, which renders this same component without a limit. WHY: a long-time customer's visits or past appointments
 * run to dozens of rows, which buried every section below them.
 *
 * @param {object} section - this section's config from group.portal_records.sections
 * @param {number} [previewLimit] - rows to show per list (appointments: per Upcoming / Past); all when omitted
 * @param {() => void} [onSeeAll] - opens the full list; shown only when rows were held back
 * @param {boolean} [showTitle] - false on the section's own page, whose header already names it
 */
const RecordSection = ({ groupId, sectionKey, section, previewLimit, onSeeAll, showTitle = true }) => {
  const [records, setRecords] = useState(null);
  const [error, setError] = useState(null);
  const [openRecordId, setOpenRecordId] = useState(null);
  // Vehicles whose service map is open; the first one starts open (null = not chosen yet).
  const [expandedIds, setExpandedIds] = useState(null);

  useEffect(() => {
    let cancelled = false;
    // Clear the last group's rows so they never show under another group while this one loads.
    setRecords(null);
    setError(null);
    setExpandedIds(null);
    recordsService
      .getRecords(groupId, sectionKey)
      .then((results) => {
        if (!cancelled) setRecords(results || []);
      })
      .catch(() => {
        if (!cancelled) setError(`We couldn't load ${(SECTION_TITLES[sectionKey] || 'these records').toLowerCase()} right now.`);
      });
    return () => {
      cancelled = true;
    };
  }, [groupId, sectionKey]);

  const [primaryField, secondaryField] = section?.fields || [];

  if (records !== null && records.length === 0) return null;

  // Records arrive newest first (appointments: upcoming soonest-first, then past most-recent-first), so a slice from
  // the front is "the most recent N".
  const limit = (rows) => (previewLimit ? rows.slice(0, previewLimit) : rows);
  const groups =
    records && sectionKey === 'appointments'
      ? TIMEFRAME_GROUPS.map((group) => ({ ...group, rows: records.filter((record) => record.timeframe === group.key) }))
      : null;
  const shownCount = !records ? 0 : groups ? groups.reduce((count, group) => count + limit(group.rows).length, 0) : limit(records).length;
  const hiddenCount = records ? records.length - shownCount : 0;

  const showServiceMap = sectionKey === 'vehicles' && section?.service_map;
  const expanded = expandedIds ?? new Set(records?.length ? [records[0].id] : []);
  const toggleExpanded = (recordId) => {
    const next = new Set(expanded);
    if (next.has(recordId)) next.delete(recordId);
    else next.add(recordId);
    setExpandedIds(next);
  };

  // A vehicle with the company's service map on: a card named for the car, its next field, and the map beneath.
  const otherVehicleField = (section?.fields || []).find((field) => !VEHICLE_NAME_FIELDS.includes(field.id));
  const renderVehicleCard = (record) => {
    const name = VEHICLE_NAME_FIELDS.map((id) => record[id])
      .filter(Boolean)
      .join(' ');
    const [titleField, subtitleField] = name ? [null, otherVehicleField] : [primaryField, secondaryField];
    const title = name || (titleField ? formatFieldValue(record[titleField.id], titleField.type, titleField.id) : EMPTY_VALUE);
    const mapOpen = expanded.has(record.id);
    return (
      <Card variant="outlined" key={record.id} sx={{ borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.5, py: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontSize: 19, fontWeight: 600 }}>
              {title}
            </Typography>
            {subtitleField && (
              <Typography variant="body2" color="text.secondary">
                {subtitleField.name}: {formatFieldValue(record[subtitleField.id], subtitleField.type, subtitleField.id)}
              </Typography>
            )}
          </Box>
          {/* One group, so on a phone both buttons wrap under the name together instead of splitting across lines. */}
          <Box sx={{ display: 'flex', gap: 1, ml: -0.75 }}>
            {/* Labels name the vehicle: every card has the same two buttons, which a screen reader can't tell apart. */}
            <Button size="small" onClick={() => setOpenRecordId(record.id)} aria-label={`Details for ${title}`}>
              Details
            </Button>
            <Button
              size="small"
              onClick={() => toggleExpanded(record.id)}
              aria-expanded={mapOpen}
              aria-label={`${mapOpen ? 'Hide service map' : 'Service map'} for ${title}`}
              endIcon={<ExpandMoreIcon sx={{ transform: mapOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />}
            >
              {mapOpen ? 'Hide service map' : 'Service map'}
            </Button>
          </Box>
        </Box>
        <Collapse in={expanded.has(record.id)} unmountOnExit>
          <Box sx={{ borderTop: 1, borderColor: 'divider', px: 2.5, py: 2.75 }}>
            <VehicleServiceMap groupId={groupId} vehicleId={record.id} />
          </Box>
        </Collapse>
      </Card>
    );
  };

  const renderRow = (record) => (
    <Card variant="outlined" key={record.id}>
      <CardActionArea onClick={() => setOpenRecordId(record.id)} sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1.5 }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, ...TWO_LINES }}>
            {primaryField ? formatFieldValue(record[primaryField.id], primaryField.type, primaryField.id) : EMPTY_VALUE}
          </Typography>
          {secondaryField && (
            <Typography variant="body2" color="text.secondary" sx={TWO_LINES}>
              {secondaryField.name}: {formatFieldValue(record[secondaryField.id], secondaryField.type, secondaryField.id)}
            </Typography>
          )}
        </Box>
        <ChevronRightIcon color="action" />
      </CardActionArea>
    </Card>
  );

  return (
    <Box sx={{ mt: 4 }}>
      {showTitle && (
        <Typography variant="h5" gutterBottom>
          {SECTION_TITLES[sectionKey]}
        </Typography>
      )}

      {records === null && !error && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      )}
      {error && <Alert severity="error">{error}</Alert>}

      {records &&
        (groups ? (
          groups.map(({ key, label, rows }) =>
            rows.length ? (
              <Box key={key} sx={{ mb: 2 }}>
                <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  {label}
                </Typography>
                <Stack spacing={1}>{limit(rows).map(renderRow)}</Stack>
              </Box>
            ) : null,
          )
        ) : (
          <Stack spacing={showServiceMap ? 1.75 : 1}>{limit(records).map(showServiceMap ? renderVehicleCard : renderRow)}</Stack>
        ))}

      {hiddenCount > 0 && onSeeAll && (
        <Button onClick={onSeeAll} sx={{ mt: 1 }}>
          See all {records.length}
        </Button>
      )}

      <RecordDetailModal
        groupId={groupId}
        sectionKey={sectionKey}
        section={section}
        recordId={openRecordId}
        onClose={() => setOpenRecordId(null)}
      />
    </Box>
  );
};

export default RecordSection;
